import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { LocalStorage } from 'angular-web-storage';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ScheduledEvent, ScheduledEventPaymentStatus, ScheduledEventReservation } from '../../../../../../../../../models/scheduled-event';
import { batchAll, CallbackResult, CallbackResultStatus, capitalizeFirstLetter, removeDuplicates } from '../../../../../../../../../utils';
import {
  Customer,
  CustomerActiveProficiency,
  CustomerMembershipIcon,
  CustomerProfileDetails,
  getCustomerCustomFieldValue
} from '../../../../../../../../../models/customer';
import { RebookModalComponent } from '../../../../../modals/rebook-modal/rebook-modal.component';
import {
  ParticipantDetailsModalComponent,
  PARTICIPANT_DETAILS_QUERY_PARAM
} from '../../../../../modals/participant-details-modal/participant-details-modal.component';
import {
  getCustomerFieldValue,
  getScheduledEventsColumnHeaderName,
  openClimberAppInNewTabAsCustomer,
  openClimberAppInNewTabAsStaff
} from '../../../../../../../../../utils-models';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import {
  ReviewUnlinkedReservationModalComponent
} from '../../../../../../inbox/inbox-events/inbox-unlinked-reservations/modals/review-unlinked-reservation-modal/review-unlinked-reservation-modal.component';
import { InboxService } from '../../../../../../../../../services/inbox/inbox.service';
import {
  ExportEventAttendeesModalComponent
} from '../../../../../modals/export-event-attendees-modal/export-event-attendees-modal.component';
import { CreateBookingModalComponent } from '../../../../../modals/create-booking-modal/create-booking-modal.component';
import { AddParticipantToPrivateEventModalComponent } from '../../../../../modals/add-participant-to-private-event-modal/add-participant-to-private-event-modal.component';
import { ScheduledEventService } from '../../../../../../../../../services/scheduled-event/scheduled-event.service';
import {
  CheckInAlertsModalComponent
} from '../../../../../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-alerts-modal/check-in-alerts-modal.component';
import {
  CheckInNoEntryMethodModalComponent
} from '../../../../../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-no-entry-method-modal/check-in-no-entry-method-modal.component';
import { ScheduledEventsColumnType } from '../../../../../../../../../models/event-type-enums';
import {
  BulkUploadEventParticipantsModalComponent
} from './modals/bulk-upload-event-participants-modal/bulk-upload-event-participants-modal.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  InviteParticipantsToAnotherEventModalComponent
} from './modals/invite-participants-to-another-event-modal/invite-participants-to-another-event-modal.component';
import {
  InviteToEventModalComponent
} from './modals/invite-to-event-modal/invite-to-event-modal.component';
import { ScheduleType } from '../../../../../../../../../models/event-type-enums';
import { AddToRosterModalComponent } from '../../../../../modals/add-to-roster-modal/add-to-roster-modal.component';
import { InviteToRosterModalComponent } from '../../../../../modals/invite-to-roster-modal/invite-to-roster-modal.component';
import { ProfileField } from '../../../../../../../../../models/organization-config-enums';


@Component({
  selector: 'app-event-participants-sub-list',
  templateUrl: './event-participants-sub-list.component.html',
  styleUrls: ['./event-participants-sub-list.component.css']
})
export class EventParticipantsSubListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() event: ScheduledEvent;
  @Input() resultCallback: (result: CallbackResult, reload: boolean) => void;

  // Held on the parent (event-detail) because this component is destroyed and
  // recreated on each reload; toggling invokes the callback so the parent
  // re-fetches the event with the matching query parameter (PRO-1225).
  @Input() showNonpaymentCancelledReservations = false;
  @Input() showNonpaymentCancelledChangeCallback: (value: boolean) => void;

  // The reservation part to check in once the reloaded event contains its row.
  // Two-way bound to the parent because the parent's `*ngIf` recreates this
  // component on each reload, so the pending value cannot live here (PRO-1220).
  @Input() pendingCheckInReservationPartId: number | null = null;
  @Output() pendingCheckInReservationPartIdChange = new EventEmitter<number | null>();
  // The deferred check-in starts at most once per instance, however many
  // lifecycle hooks fire.
  private pendingCheckInScheduled = false;

  @ViewChild('participantTableWrapper') participantTableWrapper;
  @ViewChild('participantTableTitleContainer') participantTableTitleContainer;

  public loadListSubject = new Subject<null>();

  public pageNumber = 1;
  // Giving these project-unique names ensures they are not shared with other
  // similar variables.
  @LocalStorage() public pageSizeEventParticipantsSubList;
  @LocalStorage() public autoLocationCheckInEventParticipantsSubList;
  public search = '';

  public customer: {[id: number]: Customer} = {};
  public customerProfileDetails: {[id: number]: CustomerProfileDetails} = {};
  public customerIdsWithoutParticipantAgreements = new Set<number>();
  public customerIdToWaiverAlert = new Map<number, string>();
  // Danger red (matches .text-danger) so missing-document alerts stand out from
  // the standard yellow profile alert (PRO-1263).
  public readonly missingDocumentAlertColor = '#D0021B';

  public loadingCustomers = false;
  public loadingCustomerProfileDetails: {[id: number]: boolean} = {};
  public customerNotFound: {[id: number]: boolean} = {};
  public loadingUnlinkedReservation: {[id: number]: boolean} = {};

  public reloadNumber = 0;
  public serverError: number = null;

  public ScheduledEventPaymentStatus = ScheduledEventPaymentStatus;
  public ProfileField = ProfileField;
  public capitalizeFirstLetter = capitalizeFirstLetter;
  public ScheduledEventsColumnType = ScheduledEventsColumnType;
  public ScheduleType = ScheduleType;
  public getScheduledEventsColumnHeaderName = getScheduledEventsColumnHeaderName;
  public getCustomerCustomFieldValue = getCustomerCustomFieldValue;
  public Array = Array;

  // Participant details modal (PRO-1217) state. The reservation part ID of the
  // currently open modal is mirrored in the URL so the modal can be deep-linked.
  private participantDetailsModalRef: NgbModalRef = null;
  private currentlyOpenReservationPartId: number = null;
  private desiredParticipantReservationPartId: number = null;
  private queryParamSubscription: Subscription = null;
  // Set when the modal is closed by an action that navigates away (e.g.
  // Cancel), so the close handler doesn't fire a competing navigation to clear
  // the query parameter.
  private suppressParticipantDetailsParamClear = false;

  constructor(
    private orgService: OrganizationConfigService,
    private customerService: CustomerService,
    private inboxService: InboxService,
    private scheduledEventService: ScheduledEventService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    if (!this.pageSizeEventParticipantsSubList) {
      this.pageSizeEventParticipantsSubList = 10;
    }
    // This is needed in order for the variable to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeEventParticipantsSubList = this.pageSizeEventParticipantsSubList;

    if (!this.autoLocationCheckInEventParticipantsSubList) {
      this.autoLocationCheckInEventParticipantsSubList = true;
    }
    // This is needed in order for the variable to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.autoLocationCheckInEventParticipantsSubList = this.autoLocationCheckInEventParticipantsSubList;

    this.loadListSubject.pipe(
      debounceTime(100)
    ).subscribe(() => {
      this.loadReservationCustomerInfo();
    })

    this.loadListSubject.next();

    // Open/close the participant details modal in response to the URL query
    // parameter, so the modal can be deep-linked and shared.
    this.queryParamSubscription = this.route.queryParamMap.subscribe(params => {
      this.readParticipantDetailsQueryParam(params);
      this.syncParticipantDetailsModal();
    });

    // A recreated instance may already hold the newly-booked participant's row.
    this.checkInPendingParticipantIfReady();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadListSubject.next();

    // The event's reservations may have just loaded.
    this.syncParticipantDetailsModal();
    this.checkInPendingParticipantIfReady();
  }

  ngAfterViewInit() {
    this.updateTableTitleContainerWidth();
    // Initial call can make the title container a little too small - might be
    // related to the scrollbar?
    setTimeout(
      () => { this.updateTableTitleContainerWidth(); },
      100
    );
  }

  ngOnDestroy() {
    // Halt any in-progress loads if the component is destroyed.
    this.reloadNumber = -1;

    if (this.queryParamSubscription) {
      this.queryParamSubscription.unsubscribe();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTableTitleContainerWidth();
  }

  /**
   * PRO-1225: Notify the parent that the "show nonpayment-cancelled
   * reservations" toggle changed, so it can re-fetch the event.
   */
  public onShowNonpaymentCancelledChange(value: boolean) {
    if (this.showNonpaymentCancelledChangeCallback) {
      this.showNonpaymentCancelledChangeCallback(value);
    }
  }

  public filteredReservations(): ScheduledEventReservation[] {
    const filterItems = this.search.toLowerCase().split(' ');

    return this.event.reservations.filter(res => {
      for (const filter of filterItems) {
        const filterMatches = (
          (res.first_name + ' ' + res.last_name).toLowerCase().includes(filter) ||
          res.email.toLowerCase().includes(filter) ||
          res.registration_number.toLowerCase().includes(filter)
        );
        if (!filterMatches) { return false; }
      }

      return true;
    }).slice(
      (this.pageNumber - 1) * this.pageSizeEventParticipantsSubList,
      this.pageNumber * this.pageSizeEventParticipantsSubList
    );
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    return Math.ceil(
      this.event.reservations.length / this.pageSizeEventParticipantsSubList
    );
  }

  public openRebookModal(reservation: ScheduledEventReservation) {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(RebookModalComponent);
    modalRef.componentInstance.bookingId = reservation.booking_id;
    modalRef.componentInstance.savedCallback = () => {
      this.resultCallback(
        {
          text: 'Reservation rebooked successfully.',
          status: CallbackResultStatus.success
        },
        true
      );
    };
  }

  /**
   * Open the participant details modal for the given reservation, wiring up the
   * footer action callbacks to the equivalent actions in this table.
   *
   * @param reservation - The reservation to show details for.
   * @param writeUrl - Whether to write the reservation part ID into the URL.
   *   This is true when opened via the View button, and false when opened in
   *   response to the URL already containing the parameter (deep linking).
   */
  public openParticipantDetailsModal(reservation: ScheduledEventReservation,
                                     writeUrl: boolean = true) {
    if (this.currentlyOpenReservationPartId === reservation.reservation_part_id) {
      return;
    }

    // Close any modal already open (e.g. when switching directly between
    // participants via the URL).
    if (this.participantDetailsModalRef) {
      this.participantDetailsModalRef.dismiss();
    }

    this.currentlyOpenReservationPartId = reservation.reservation_part_id;

    if (writeUrl) {
      this.setParticipantDetailsQueryParam(reservation.reservation_part_id);
    }

    const modalRef = this.modalService.open(
      ParticipantDetailsModalComponent,
      { size: 'lg', scrollable: true }
    );
    this.participantDetailsModalRef = modalRef;

    modalRef.componentInstance.reservationPartId = reservation.reservation_part_id;
    modalRef.componentInstance.reservation = reservation;
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.currentPath = this.router.url.split('?')[0];
    modalRef.componentInstance.toggleCheckInCallback =
      () => this.toggleReservationCheckIn(reservation);
    modalRef.componentInstance.completePaymentCallback =
      () => this.goToCompletePaymentAsStaff(reservation);
    modalRef.componentInstance.rebookCallback =
      () => this.openRebookModal(reservation);
    modalRef.componentInstance.cancelCallback =
      () => this.handleGoToCancellation(reservation);

    // The guard ignores a stale close after a different modal was opened.
    const handleClosed = () => {
      if (this.participantDetailsModalRef === modalRef) {
        this.participantDetailsModalRef = null;
        this.currentlyOpenReservationPartId = null;
        if (this.suppressParticipantDetailsParamClear) {
          // The action navigated away and already dropped the query parameter;
          // clearing it here would race with that navigation.
          this.suppressParticipantDetailsParamClear = false;
        } else {
          this.clearParticipantDetailsQueryParam();
        }
      }
    };
    modalRef.result.then(handleClosed, handleClosed);
  }

  /**
   * Navigate to the cancellation and refund page for the given reservation,
   * matching the relative "Cancel" link in the participants table.
   */
  public handleGoToCancellation(reservation: ScheduledEventReservation) {
    // The modal's close handler must not override this navigation.
    this.suppressParticipantDetailsParamClear = true;
    this.router.navigate(
      [
        'booking',
        reservation.booking_id,
        reservation.reservation_id,
        'manage-cancellation-and-refund'
      ],
      { relativeTo: this.route }
    );
  }

  private readParticipantDetailsQueryParam(params: ParamMap) {
    const rawValue = params.get(PARTICIPANT_DETAILS_QUERY_PARAM);
    const parsed = rawValue ? parseInt(rawValue, 10) : NaN;
    this.desiredParticipantReservationPartId = isNaN(parsed) ? null : parsed;
  }

  /**
   * Open or close the participant details modal so that it matches the
   * reservation part ID currently in the URL.
   */
  private syncParticipantDetailsModal() {
    const desiredId = this.desiredParticipantReservationPartId;

    if (desiredId === this.currentlyOpenReservationPartId) {
      return;
    }

    if (desiredId === null) {
      if (this.participantDetailsModalRef) {
        this.participantDetailsModalRef.dismiss();
      }
      return;
    }

    if (!this.event || !this.event.reservations) {
      return;
    }
    const reservation = this.event.reservations.find(
      res => res.reservation_part_id === desiredId
    );
    if (!reservation) {
      return;
    }

    this.openParticipantDetailsModal(reservation, false);
  }

  private setParticipantDetailsQueryParam(reservationPartId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [PARTICIPANT_DETAILS_QUERY_PARAM]: reservationPartId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearParticipantDetailsQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [PARTICIPANT_DETAILS_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * "Complete Payment" is an anchor at the booking's tokenized, login-free
   * payment URL so staff can copy a shareable link (PRO-1388). A plain
   * left-click is routed through the Climber App login-token flow instead:
   * the raw href opens as an anonymous visitor, with no card reader and no
   * account credit. Modified clicks are left to the browser.
   */
  public handleCompletePaymentClick(reservation: ScheduledEventReservation,
                                    clickEvent: MouseEvent) {
    if (clickEvent.ctrlKey || clickEvent.metaKey || clickEvent.shiftKey ||
        clickEvent.altKey || (clickEvent.button !== 0)) {
      return;
    }
    clickEvent.preventDefault();

    this.goToCompletePaymentAsStaff(reservation);
  }

  /**
   * Open the booking's tokenized payment page in a new tab, via the login-token
   * flow so the Climber App session carries the acting staff user. Also invoked
   * from the participant details modal's footer.
   */
  public goToCompletePaymentAsStaff(reservation: ScheduledEventReservation) {
    const baseUrl = this.orgService.getOrganizationConfig().climber_app_base_url;
    const paymentUrl = reservation.booking_payment_url;

    let tokenPromise: Promise<void>;
    if (reservation.booked_by_id !== null) {
      tokenPromise = this.customerService
        .getCustomerClimberAppToken(reservation.booked_by_id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            reservation.booked_by_id,
            baseUrl,
            paymentUrl
          );
        });
    } else {
      tokenPromise = this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            baseUrl,
            paymentUrl
          );
        });
    }
    tokenPromise
      .catch(reason => {
        this.serverError = reason.status;
      });
  }

  public openReviewUnlinkedReservationModal(reservation: ScheduledEventReservation) {
    this.resultCallback(null, false);

    this.loadingUnlinkedReservation[reservation.reservation_id] = true;

    this.inboxService
      .getUnlinkedReservation(reservation.reservation_id)
      .then(unlinkedReservation => {
        const modalRef = this.modalService.open(ReviewUnlinkedReservationModalComponent);
        modalRef.componentInstance.unlinkedReservation = unlinkedReservation;
        modalRef.componentInstance.showHideOption = false;
        modalRef.componentInstance.savedCallback = () => {
          // Ensure the account link updates.
          this.resultCallback(
            {
              text: 'Reservation linked successfully.',
              status: CallbackResultStatus.success
            },
            true
          );

          // Set the loading state back to true - will be reset when the event reloads.
          this.loadingUnlinkedReservation[reservation.reservation_id] = true;
        };
      })
      .catch(reason => {
        if (reason.status === 404) {
          // 404 indicates that the reservation is no longer unlinked, so reload.
          this.resultCallback(null, true);
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingUnlinkedReservation[reservation.reservation_id] = false;
      });
  }

  public openExportAttendeeListModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(ExportEventAttendeesModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.customers = this.customer;
    modalRef.componentInstance.customerProfileDetails = this.customerProfileDetails;
    modalRef.componentInstance.savedCallback = () => {
      this.resultCallback(
        {
          text: 'Attendees exported successfully.',
          status: CallbackResultStatus.success
        },
        false
      );
    };
  }

  public openCreateBookingModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(CreateBookingModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.bookingCreatedCallback = (result: CallbackResult,
                                                         checkInReservationPartId: number | null) => {
      // Recorded on the parent so the check-in intent survives the reload
      // recreating this component; see `checkInPendingParticipantIfReady`.
      if (checkInReservationPartId !== null) {
        this.pendingCheckInReservationPartIdChange.emit(checkInReservationPartId);
      }

      this.resultCallback(
        result,
        true
      );
    };
  }

  /**
   * Open the "Add Participant" modal for this private event (PRO-1221). The
   * booking is loaded first: the modal needs it to find the first open slot
   * and guard against duplicates.
   */
  public openAddParticipantToPrivateEventModal() {
    this.resultCallback(null, false);

    this.scheduledEventService
      .getPrivateEventBooking(this.event.event_id)
      .then(booking => {
        const modalRef = this.modalService.open(AddParticipantToPrivateEventModalComponent);
        modalRef.componentInstance.privateEventId = this.event.event_id;
        modalRef.componentInstance.booking = booking;
        modalRef.componentInstance.eventPartId = this.event.event_part_id;
        modalRef.componentInstance.eventPartStartDatetime = this.event.start_datetime;
        modalRef.componentInstance.participantAddedCallback = (result: CallbackResult,
                                                              checkInReservationPartId: number | null) => {
          // Recorded on the parent so the check-in intent survives the reload
          // recreating this component; see `checkInPendingParticipantIfReady`.
          if (checkInReservationPartId !== null) {
            this.pendingCheckInReservationPartIdChange.emit(checkInReservationPartId);
          }

          this.resultCallback(result, true);
        };
      })
      .catch(reason => {
        const text = (reason.status === 403)
          ? 'You do not have permission to add a participant.'
          : 'Unable to load the private event booking. Please try again.';
        this.resultCallback(
          {
            text,
            status: CallbackResultStatus.error
          },
          false
        );
      });
  }

  /**
   * After a booking made with "check in immediately", check the new
   * participant in once the reloaded event contains their row (PRO-1220).
   * Reuses toggleReservationCheckIn so the automatic facility check-in happens
   * exactly as if the table's Check In button were clicked.
   */
  private checkInPendingParticipantIfReady() {
    if (this.pendingCheckInScheduled) { return; }
    if (this.pendingCheckInReservationPartId === null) { return; }
    if (!this.event || !this.event.reservations) { return; }

    const newReservation = this.event.reservations.find(
      res => res.reservation_part_id === this.pendingCheckInReservationPartId
    );

    // The reload may not have completed yet; wait for a later lifecycle hook.
    if (!newReservation) { return; }

    this.pendingCheckInScheduled = true;

    // Deferred: clearing the parent's two-way-bound intent mid change
    // detection throws ExpressionChangedAfterItHasBeenChecked.
    setTimeout(() => {
      this.pendingCheckInReservationPartIdChange.emit(null);

      // Already checked in (a redundant reload): toggling would check them out.
      if (!newReservation.has_checked_in) {
        this.toggleReservationCheckIn(newReservation);
      }
    });
  }

  public openAddToRosterModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(AddToRosterModalComponent);
    modalRef.componentInstance.eventTypeId = this.event.event_type_id;
    modalRef.componentInstance.eventTypeName = this.event.name;
    modalRef.componentInstance.preselectedRosterId = this.event.roster_id;
  }

  public openInviteToRosterModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(InviteToRosterModalComponent);
    modalRef.componentInstance.eventTypeId = this.event.event_type_id;
    modalRef.componentInstance.eventTypeName = this.event.name;
    modalRef.componentInstance.preselectedRosterId = this.event.roster_id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    }
  }

  public openBulkUploadParticipantsModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(BulkUploadEventParticipantsModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
    modalRef.componentInstance.reloadCallback = () => {
      this.resultCallback(null, true);
    };
  }

  public openInviteParticipantsToAnotherEventModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(InviteParticipantsToAnotherEventModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  public openInviteToEventModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(InviteToEventModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  /**
   * Whether the given reservation is an "open slot" on a private event: a
   * placeholder reservation slot with no participant (customer) attached yet.
   * Such slots cannot be checked in until a participant is added to the booking.
   */
  public isPrivateEventOpenSlot(reservation: ScheduledEventReservation): boolean {
    return this.event.is_private && !reservation.customer_id;
  }

  public toggleReservationCheckIn(reservation: ScheduledEventReservation) {
    reservation.has_checked_in = !reservation.has_checked_in;
    reservation.checkInLoading = true;

    this.event.check_in_count = this.event.reservations.filter(res => res.has_checked_in).length;

    this.scheduledEventService
      .setReservationPartCheckIn(
        reservation.reservation_part_id,
        reservation.has_checked_in
      )
      .then(response => {
        const promptForGymCheckIn = (
          reservation.has_checked_in &&
          reservation.customer_id &&
          !response.is_checked_in_to_gym &&
          this.autoLocationCheckInEventParticipantsSubList
        );
        if (promptForGymCheckIn) {
          return this.checkInCustomerInternal(reservation, false, null);
        }
      })
      .catch(reason => {
        // If there is an error, do not show an error message, but revert the
        // checkbox.
        reservation.has_checked_in = !reservation.has_checked_in;
      })
      .finally(() => { reservation.checkInLoading = false; });
  }

  public getCustomerFieldValueForId(field: ProfileField, customerIdInt: number) {
    return getCustomerFieldValue(
      field,
      this.customer[customerIdInt],
      this.customerProfileDetails[customerIdInt],
      (
        this.loadingCustomers ||
        this.loadingCustomerProfileDetails[customerIdInt] ||
        !this.customer[customerIdInt] ||
        !this.customerProfileDetails[customerIdInt]
      )
    );
  }

  public getCustomerProficienciesForId(customerIdInt: number): CustomerActiveProficiency[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].active_proficiencies;
  }

  public getCustomerMembershipIconsForId(customerIdInt: number): CustomerMembershipIcon[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].membership_icons;
  }

  public allCustomersAreLoaded() {
    for (const reservation of this.event.reservations) {
      if (reservation.customer_id) {
        if (this.loadingCustomers) {
          return false;
        }
        if (this.loadingCustomerProfileDetails[reservation.customer_id]) {
          return false;
        }
      }
    }

    return true;
  }

  public allowNameWrapping(firstName: string, lastName: string): boolean {
    return (firstName.length + lastName.length) > 25;
  }

  /**
   * Cancelled reservations are excluded (PRO-1410) so staff don't accidentally
   * email customers whose booking was cancelled. Cancelling the event itself
   * cancels every one of its reservations, though, so the exclusion is skipped
   * there — that's exactly when staff need to reach the whole list.
   */
  public getParticipantMailList(includeBookingCustomers: boolean): string {
    const activeReservations = this.event.is_cancelled
      ? this.event.reservations
      : this.event.reservations.filter(res => !res.is_cancelled);

    let mailList = activeReservations
      .filter(res => res.email)
      .map(res => res.email);

    if (includeBookingCustomers) {
      mailList = mailList.concat(
        activeReservations
          .filter(res => res.booked_by_email)
          .map(res => res.booked_by_email)
      );
    }

    return removeDuplicates(mailList).join(',');
  }

  public handleEmailsCopiedToClipboard() {
    this.resultCallback(
      {
        text: 'Emails copied to clipboard.',
        status: CallbackResultStatus.success
      },
      false
    );
  }

  public handleRegistrationLinkCopiedToClipboard() {
    this.resultCallback(
      {
        text: 'Climber App registration link copied to clipboard.',
        status: CallbackResultStatus.success
      },
      false
    );
  }

  public getQuestionResponse(res: ScheduledEventReservation, questionName: string): string | undefined {
    let resp = res.question_responses[questionName];
    if (resp === undefined) {
      resp = res.booking_question_responses[questionName];
    }
    return resp;
  }

  private loadReservationCustomerInfo() {
    this.reloadNumber++;
    const thisReloadNumber = this.reloadNumber;

    this.loadingCustomers = true;
    const customerIdsToLoad: number[] = [];
    const customerIdsLoaded: {[key: number]: boolean} = {};

    for (const reservation of this.event.reservations) {
      if (reservation.customer_id && !(reservation.customer_id in customerIdsLoaded)) {
        customerIdsToLoad.push(reservation.customer_id);
        customerIdsLoaded[reservation.customer_id] = false;
      }
    }

    if (customerIdsToLoad.length === 0) {
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .bulkRetrieveCustomersById(customerIdsToLoad)
      .then(result => {
        for (const customer of result.results) {
          this.customer[customer.id] = customer;
          customerIdsLoaded[customer.id] = true;
        }
        for (const customerId of customerIdsToLoad) {
          if (!customerIdsLoaded[customerId]) {
            this.customerNotFound[customerId] = true;
          }
        }
      })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingCustomers = false; });

    if (this.event.participant_agreement_template_id) {
      this.customerService
        .findCustomersWithoutDocument(
          customerIdsToLoad,
          this.event.participant_agreement_template_id
        )
        .then(result => {
          for (const cid of result) {
            this.customerIdsWithoutParticipantAgreements.add(cid);
          }
        });
    }

    if (this.event.requires_waiver) {
      const eventStartDate = this.event.start_datetime.split('T')[0];
      this.customerService
        .findCustomersWithWaiverIssues(
          customerIdsToLoad,
          this.event.location_id,
          eventStartDate
        )
        .then(result => {
          for (const cidStr of Object.keys(result)) {
            this.customerIdToWaiverAlert.set(Number(cidStr), result[cidStr]);
          }
        });
    }

    const loadProfileDetailsHandler = (customerId: number): Promise<void> => {
      if (thisReloadNumber === this.reloadNumber) {
        return this.loadCustomerProfileDetails(customerId);
      }
    };

    batchAll(
      4,
      customerIdsToLoad,
      loadProfileDetailsHandler
    );
  }

  /**
   * Load the profile details of the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomerProfileDetails(customerIdInt: number): Promise<void> {
    this.loadingCustomerProfileDetails[customerIdInt] = true;

    this.customerNotFound[customerIdInt] = false;

    return this.customerService
      .getCustomerProfileDetails(customerIdInt)
      .then(customerProfileDetails => {
        this.customerProfileDetails[customerIdInt] = customerProfileDetails;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingCustomerProfileDetails[customerIdInt] = false;
      });
  }

  private checkInCustomerInternal(reservation: ScheduledEventReservation,
                                  bypassAlerts: boolean,
                                  freeEntryReason?: string) {
    return this.customerService
      .checkInCustomer(
        reservation.customer_id,
        bypassAlerts,
        freeEntryReason
      )
      .then(() => {
        this.resultCallback(
          {
            text: (
              reservation.first_name +
              ' ' +
              reservation.last_name +
              ' was checked into the facility, in addition to the event.'
            ),
            status: CallbackResultStatus.success
          },
          false
        );
      })
      .catch(reason => {
        const errorType = reason.error.error;
        if (errorType === 'check_in_alerts') {
          const checkInAlerts = reason.error.alerts;
          const modalRef = this.modalService.open(CheckInAlertsModalComponent);
          modalRef.componentInstance.alerts = checkInAlerts;
          modalRef.componentInstance.checkInAnywayCallback = () => {
            this.checkInCustomerInternal(reservation, true, freeEntryReason);
          };
        }
        else if (errorType === 'no_entry_method') {
          const modalRef = this.modalService.open(CheckInNoEntryMethodModalComponent);
          modalRef.componentInstance.customerId = reservation.customer_id;
          modalRef.componentInstance.provideFreeEntryCallback = (thisFreeEntryReason: string) => {
            this.checkInCustomerInternal(reservation, bypassAlerts, thisFreeEntryReason);
          };
        }
      });
  }

  private updateTableTitleContainerWidth() {
    this.participantTableTitleContainer.nativeElement.style.width =
      this.participantTableWrapper.nativeElement.offsetWidth.toString() + 'px';
  }
}
